/**
 * PiP — Game client embedded in the site.
 *
 * Two modes:
 *   1. Embedded — part of the site flow (default). No drag, no resize.
 *   2. Floating — detached fixed-position window (activated by pop-out).
 *
 * Native browser PiP is handled by pip-window.js.
 *
 * @module ui/pip
 */

const LS_KEY = 'pocketdungeon.pip';

function loadBounds() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveBounds(bounds) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(bounds));
  } catch { /* ignore */ }
}

/**
 * @param {object} opts
 * @param {number} [opts.defaultW]
 * @param {number} [opts.defaultH]
 * @param {{ onPopOut?: () => void, onPopIn?: () => void }} [opts.callbacks]
 * @returns {PiP}
 */
export function createPiP(opts = {}) {
  const el = document.getElementById('pd-pip');
  if (!el) throw new Error('PiP: #pd-pip element not found in DOM.');

  const titlebar = el.querySelector('.pd-pip-titlebar');
  const btnPopOut = el.querySelector('.pd-pip-popout');
  const btnPopIn = el.querySelector('.pd-pip-popin');
  const toolbar = el.querySelector('.pd-pip-toolbar');
  const btnMenuToggle = el.querySelector('.pd-pip-menu-toggle');

  // Add resize handle (only visible in floating mode)
  let resizeHandle = el.querySelector('.pd-pip-resize');
  if (!resizeHandle) {
    resizeHandle = document.createElement('div');
    resizeHandle.className = 'pd-pip-resize';
    el.appendChild(resizeHandle);
  }

  const minW = 320, minH = 200;
  const defaultW = opts.defaultW ?? 400;
  const defaultH = opts.defaultH ?? 250;

  const callbacks = opts.callbacks ?? {};

  // --- State ---
  let floating = false;
  let poppedOut = false;
  let dragging = false;
  let resizing = false;
  let dragOffX = 0, dragOffY = 0;
  let resizeStartW = 0, resizeStartH = 0, resizeStartX = 0, resizeStartY = 0;

  // --- Floating mode (detach from site) ---

  function enterFloating() {
    if (floating) return;
    floating = true;
    el.classList.add('pd-pip--floating');

    // Position in centre of viewport
    const stored = loadBounds();
    if (stored && stored.w >= minW && stored.h >= minH) {
      el.style.left = stored.x + 'px';
      el.style.top = stored.y + 'px';
      el.style.width = stored.w + 'px';
      el.style.height = stored.h + 'px';
    } else {
      const x = Math.round((window.innerWidth - defaultW) / 2);
      const y = Math.round((window.innerHeight - defaultH) / 2);
      el.style.left = x + 'px';
      el.style.top = y + 'px';
      el.style.width = defaultW + 'px';
      el.style.height = defaultH + 'px';
    }
  }

  function exitFloating() {
    if (!floating) return;
    floating = false;
    el.classList.remove('pd-pip--floating');
    el.style.left = '';
    el.style.top = '';
    el.style.width = '';
    el.style.height = '';
  }

  // ---- Drag (only in floating mode) ----

  function onDragStart(e) {
    if (!floating || e.target.closest('button')) return;
    dragging = true;
    const rect = el.getBoundingClientRect();
    dragOffX = e.clientX - rect.left;
    dragOffY = e.clientY - rect.top;
    el.classList.add('dragging');
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onDragMove(e) {
    if (!dragging) return;
    const x = Math.max(0, Math.min(e.clientX - dragOffX, window.innerWidth - el.offsetWidth));
    const y = Math.max(0, Math.min(e.clientY - dragOffY, window.innerHeight - el.offsetHeight));
    el.style.left = x + 'px';
    el.style.top = y + 'px';
    e.preventDefault();
  }

  function onDragEnd(e) {
    if (!dragging) return;
    dragging = false;
    el.classList.remove('dragging');
    persistBounds();
  }

  titlebar.addEventListener('pointerdown', onDragStart);
  document.addEventListener('pointermove', onDragMove);
  document.addEventListener('pointerup', onDragEnd);
  document.addEventListener('pointercancel', onDragEnd);

  // ---- Resize (only in floating mode) ----

  function onResizeStart(e) {
    if (!floating) return;
    resizing = true;
    const rect = el.getBoundingClientRect();
    resizeStartW = rect.width;
    resizeStartH = rect.height;
    resizeStartX = e.clientX;
    resizeStartY = e.clientY;
    el.classList.add('resizing');
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  }

  function onResizeMove(e) {
    if (!resizing) return;
    const dw = e.clientX - resizeStartX;
    const dh = e.clientY - resizeStartY;
    el.style.width = Math.max(minW, resizeStartW + dw) + 'px';
    el.style.height = Math.max(minH, resizeStartH + dh) + 'px';
    e.preventDefault();
  }

  function onResizeEnd() {
    if (!resizing) return;
    resizing = false;
    el.classList.remove('resizing');
    persistBounds();
    window.dispatchEvent(new Event('resize'));
  }

  resizeHandle.addEventListener('pointerdown', onResizeStart);
  document.addEventListener('pointermove', onResizeMove);
  document.addEventListener('pointerup', onResizeEnd);
  document.addEventListener('pointercancel', onResizeEnd);

  // ---- Native PiP (pop-out / pop-in) ----

  // enter/exitNativeMode are pure UI-state setters, called BY the orchestration
  // (main.js onPopOut/onPopIn). They must not call back into those callbacks —
  // doing so re-entered onPopOut and left the element stuck in floating + popped
  // -out at once (the broken copy left behind the native PiP).
  function enterNativeMode() {
    poppedOut = true;
    if (floating) exitFloating();
    el.classList.add('pipped-out');
    btnPopOut.hidden = true;
    btnPopIn.hidden = false;
    const placeholder = document.getElementById('pd-pip-placeholder');
    if (placeholder) placeholder.removeAttribute('hidden');
  }

  function exitNativeMode() {
    if (!poppedOut) return;
    poppedOut = false;
    el.classList.remove('pipped-out');
    btnPopOut.hidden = false;
    btnPopIn.hidden = true;
    const placeholder = document.getElementById('pd-pip-placeholder');
    if (placeholder) placeholder.setAttribute('hidden', '');
  }

  btnPopOut?.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    // Let the callback decide: native PiP shows the inline placeholder; only the
    // unsupported/failed fallback detaches to a floating window. (Detaching here
    // unconditionally left a broken floating copy behind the native PiP.)
    callbacks.onPopOut?.();
  });

  btnPopIn?.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    callbacks.onPopIn?.();
  });

  // ---- Menu toggle (AÇÕES starts OPEN; ☰ collapses it if the player wants
  //      the slimmer window — Rafael: the action bar must show first) ----

  if (btnMenuToggle && toolbar) {
    toolbar.removeAttribute('hidden');
    btnMenuToggle.classList.add('active');
    btnMenuToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = toolbar.hasAttribute('hidden');
      toolbar.toggleAttribute('hidden', !open);
      btnMenuToggle.classList.toggle('active', open);
    });
  }

  // ---- Toolbar buttons ----

  if (toolbar) {
    toolbar.querySelectorAll('[data-panel]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const name = btn.dataset.panel;
        if (window.PD?.pipAPI) {
          window.PD.pipAPI.openPanel(name);
        } else {
          window.dispatchEvent(new CustomEvent('pd:open-panel', { detail: { name } }));
        }
      });
    });
  }

  // ---- Focus (floating mode only) ----

  let zCounter = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--z-pip')) || 50;

  function focus() {
    if (!floating) return;
    zCounter += 1;
    el.style.zIndex = zCounter;
    if (zCounter > 200) zCounter = 60;
  }

  el.addEventListener('pointerdown', focus);

  // ---- Persist ----

  function persistBounds() {
    if (!floating) return;
    saveBounds({
      x: parseInt(el.style.left) || 0,
      y: parseInt(el.style.top) || 0,
      w: parseInt(el.style.width) || defaultW,
      h: parseInt(el.style.height) || defaultH,
    });
  }

  window.addEventListener('resize', () => {
    if (!floating) return;
    const x = Math.min(parseInt(el.style.left) || 0, window.innerWidth - (parseInt(el.style.width) || defaultW));
    const y = Math.min(parseInt(el.style.top) || 0, window.innerHeight - (parseInt(el.style.height) || defaultH));
    el.style.left = Math.max(0, x) + 'px';
    el.style.top = Math.max(0, y) + 'px';
    persistBounds();
  });

  // ---- Public API ----

  return {
    el,

    /** Detach from site → floating window. */
    enterFloating,
    /** Re-attach to site → embedded. */
    exitFloating,

    /** Native PiP mode (game mirrored to browser PiP window). */
    enterNativeMode,
    exitNativeMode,

    focus,

    get floating() { return floating; },
    get poppedOut() { return poppedOut; },

    get bounds() {
      return {
        x: parseInt(el.style.left) || 0,
        y: parseInt(el.style.top) || 0,
        w: parseInt(el.style.width) || defaultW,
        h: parseInt(el.style.height) || defaultH,
      };
    },

    moveTo(x, y) {
      if (!floating) return;
      el.style.left = x + 'px';
      el.style.top = y + 'px';
      persistBounds();
    },

    resize(w, h) {
      el.style.width = Math.max(minW, w) + 'px';
      el.style.height = Math.max(minH, h) + 'px';
      persistBounds();
      window.dispatchEvent(new Event('resize'));
    },
  };
}
